/**
 * docs/ROADMAP.md B2 离线上传队列 — the IndexedDB store.
 *
 * Why IndexedDB and not localStorage: a take is a 100–800 kB `Blob`, which
 * localStorage cannot hold and base64 would inflate by a third. Why raw
 * IndexedDB and not a wrapper library: the whole surface here is five
 * operations, and `fake-indexeddb` drives this file unchanged in the tests.
 *
 * iOS Safari: everything below is plain IndexedDB + a `Blob` value. No
 * Background Sync, no Service Worker — the flush runs in the page, triggered by
 * `online` / 回到前台 / 下次打开应用 (see `store.ts`).
 */

export const DB_NAME = "xz-uploads";
export const DB_VERSION = 1;
export const STORE = "queue";

/** What a queued take is doing, as the student sees it. */
export type QueuedState =
  /** waiting for a flush (排队中) */
  | "queued"
  /** a POST is in flight (上传中) */
  | "uploading"
  /** the last attempt failed for a network reason; it will be retried (失败·重试) */
  | "failed"
  /** the server refused it for good (4xx): retrying would only fail again */
  | "rejected";

export interface QueuedUpload {
  /** The idempotency key — also this row's primary key. */
  id: string;
  /**
   * Who recorded it. A queue entry is never uploaded under another account:
   * after 换账号 on a shared phone the previous student's takes stay invisible
   * and untouched until they log back in.
   */
  user_id: string;
  assignment_id: string;
  session_id: string;
  /** For the list row — 「单字 · 诗」 rather than an opaque id. */
  title: string;
  task: string;
  duration_ms: number;
  sample_rate: number;
  /** T3 慢速 / 正常; `null` for everything else. */
  pass: string | null;
  /** The upload's `client_meta`, stored verbatim so a retry sends the same thing. */
  client_meta: Record<string, unknown>;
  filename: string;
  /**
   * The WAV bytes. Stored as an `ArrayBuffer` rather than a `Blob` on purpose:
   * several iOS Safari versions have lost `Blob` values written to IndexedDB
   * (and a `File`/`Blob` is not reliably structured-cloneable everywhere), while
   * an `ArrayBuffer` round-trips in every engine. The `Blob` is rebuilt at
   * upload time.
   */
  audio: ArrayBuffer;
  mime: string;
  created_at: number;
  state: QueuedState;
  attempts: number;
  /** Epoch ms; a flush skips entries whose backoff has not elapsed. */
  next_attempt_at: number;
  /** One plain sentence for the student when `state === "rejected"`. */
  message: string | null;
}

/**
 * `Blob` → `ArrayBuffer`. `Blob.arrayBuffer()` only arrived in Safari 14, and
 * the students on older iPhones are exactly the ones who need the queue, so
 * this falls back to `FileReader`.
 */
export function blobBytes(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === "function") return blob.arrayBuffer();
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error("could not read the take"));
    reader.readAsArrayBuffer(blob);
  });
}

/** `true` when this browser can hold a queue at all (private mode may refuse). */
export function indexedDbAvailable(): boolean {
  try {
    return typeof indexedDB !== "undefined" && indexedDB !== null;
  } catch {
    return false;
  }
}

let dbPromise: Promise<IDBDatabase> | null = null;

export function openQueueDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    if (!indexedDbAvailable()) {
      reject(new Error("IndexedDB unavailable"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "id" });
        store.createIndex("user_id", "user_id", { unique: false });
        store.createIndex("created_at", "created_at", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("could not open the upload queue"));
  });
  // A failed open must not be cached — a later attempt may succeed.
  dbPromise.catch(() => {
    dbPromise = null;
  });
  return dbPromise;
}

/** Test seam: drop the cached handle so a fresh `indexedDB` is picked up. */
export function resetQueueDb(): void {
  dbPromise = null;
}

/**
 * One transaction, one shot.
 *
 * An IndexedDB transaction auto-commits as soon as its last request settles, so
 * `await`-ing *between* requests of the same transaction either misses
 * `oncomplete` (the promise never resolves) or throws `TransactionInactiveError`
 * on the next request. Everything here therefore issues its requests
 * synchronously and resolves on `oncomplete` — which is also the point at which
 * a write is actually durable.
 */
function oneShot<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openQueueDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const txn = db.transaction(STORE, mode);
        txn.onabort = () => reject(txn.error ?? new Error("upload-queue transaction aborted"));
        txn.onerror = () => reject(txn.error ?? new Error("upload-queue transaction failed"));
        const req = run(txn.objectStore(STORE));
        req.onerror = () => reject(req.error ?? new Error("IndexedDB request failed"));
        txn.oncomplete = () => resolve(req.result);
      }),
  );
}

/** Every entry of one student, oldest first. */
export async function listFor(userId: string): Promise<QueuedUpload[]> {
  const rows = await oneShot<QueuedUpload[]>("readonly", (s) => s.getAll());
  return rows.filter((r) => r.user_id === userId).sort((a, b) => a.created_at - b.created_at);
}

export async function putEntry(entry: QueuedUpload): Promise<void> {
  await oneShot("readwrite", (s) => s.put(entry));
}

export async function deleteEntry(id: string): Promise<void> {
  await oneShot("readwrite", (s) => s.delete(id));
}

export async function getEntry(id: string): Promise<QueuedUpload | null> {
  const row = await oneShot<QueuedUpload | undefined>("readonly", (s) => s.get(id));
  return row ?? null;
}

/** Drop one student's whole queue (used by the explicit 「清空」). */
export async function clearFor(userId: string): Promise<void> {
  const rows = await listFor(userId);
  if (rows.length === 0) return;
  const db = await openQueueDb();
  await new Promise<void>((resolve, reject) => {
    const txn = db.transaction(STORE, "readwrite");
    const store = txn.objectStore(STORE);
    // Issued synchronously, in one transaction: all of them land or none do.
    for (const r of rows) store.delete(r.id);
    txn.oncomplete = () => resolve();
    txn.onabort = () => reject(txn.error ?? new Error("upload-queue transaction aborted"));
    txn.onerror = () => reject(txn.error ?? new Error("upload-queue transaction failed"));
  });
}

/**
 * `true` when the failure is the browser refusing to store more bytes.
 * Safari reports it as `QuotaExceededError`, Firefox occasionally as
 * `NS_ERROR_DOM_QUOTA_REACHED` on the legacy code.
 */
export function isQuotaError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const name = (err as { name?: unknown }).name;
  const code = (err as { code?: unknown }).code;
  return name === "QuotaExceededError" || name === "NS_ERROR_DOM_QUOTA_REACHED" || code === 22;
}
